import { creators as authCreators } from 'App/ducks/authDuck';
import { FOUNDATION } from 'config/features';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { refreshTokenAction } from './actions';
import { TIME_BEFORE_EXPIRY } from './jwtMiddleware';
import { creators, selectors } from './tokenDuck';

const logout = dispatch => {
  dispatch(authCreators.logout());
  dispatch(creators.clearToken());
};

/**
 * Proactively refreshes the JWT token in the background using a self-rescheduling
 * setTimeout. This ensures token refresh works for React Query calls, which bypass
 * Redux dispatch and therefore never trigger the jwtMiddleware.
 *
 * Does nothing when FOUNDATION mode is active or when no refresh token is present.
 */
export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const isRefreshingRef = useRef(false);
  const timeoutRef = useRef(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatch and store.getState are stable references that never change
  useEffect(() => {
    if (FOUNDATION) return;

    const scheduleNextRefresh = () => {
      const tokenObject = selectors.tokenObject(store.getState());

      if (!tokenObject.refreshToken || !tokenObject.expires) return;

      const msUntilExpiry = moment(new Date(tokenObject.expires)).diff(moment(), 'milliseconds');
      const delay = Math.max(0, msUntilExpiry - TIME_BEFORE_EXPIRY);

      timeoutRef.current = setTimeout(() => {
        if (isRefreshingRef.current) return;
        isRefreshingRef.current = true;

        refreshTokenAction(dispatch, store.getState)
          .then(() => {
            isRefreshingRef.current = false;
            scheduleNextRefresh();
          })
          .catch(() => {
            isRefreshingRef.current = false;
            logout(dispatch);
          });
      }, delay);
    };

    scheduleNextRefresh();

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);
};
